import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Calendar, Clock, ChevronDown, Building2, Search, Info } from 'lucide-react';
import { useUsers } from '../../services/queries/userQueries';
import { AUDIT_TEMPLATES } from '../../data/auditTemplates';
import { useAuditTemplates } from '../../services/queries/cyberSweepQueries';
import { formatDateForInput } from '../../types/auditTemplate';
import { useCurrentUser } from '../../hooks/useCurrentUser';

interface CreateCSAuditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (auditData: any, saveAndNew: boolean) => void;
  clientName?: string;
  clientId?: string;
}

const PHASE_STATUS_OPTIONS = [
  'Not Started',
  'Phase 1 - In Progress',
  'Phase 1 - Completed',
  'Phase 2 - In Progress',
  'Phase 2 - Completed',
  'Audit Complete',
  'Roadblocked',
];

export const CreateCSAuditModal: React.FC<CreateCSAuditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  clientName = '',
  clientId = ''
}) => {
  // Fetch real users for auditor dropdown
  const { data: users = [] } = useUsers({});
  // Get current user for Created By field
  const { userName: currentUserName } = useCurrentUser();
  // Fetch audit templates from Gobodo (prefer real templates over hardcoded)
  const { data: gobodoTemplates = [] } = useAuditTemplates();
  
  // Standard audit type names in display order
  const STANDARD_AUDIT_TYPES_ORDER = [
    'Initial Deletion',
    'Initial Audit', 
    'Final Audit',
    'Quarter #1 Audit',
    'Quarter #2 Audit',
    'Quarter #3 Audit',
    'Quarter #4 Audit',
  ];

  // Use Gobodo templates if available, fallback to local AUDIT_TEMPLATES
  const availableTemplates = useMemo(() => {
    if (gobodoTemplates.length > 0) {
      // Filter to only standard audit types and deduplicate by name
      const seen = new Set<string>();
      const filtered = gobodoTemplates
        .filter(t => {
          const isStandard = STANDARD_AUDIT_TYPES_ORDER.includes(t.name);
          const isDuplicate = seen.has(t.name);
          if (isStandard && !isDuplicate) {
            seen.add(t.name);
            return true;
          }
          return false;
        })
        .map(t => ({
          id: t.key || t.id, // Use key for Gobodo relationship
          name: t.name,
          phase1DueDateDays: t.phase1DueDateDays,
          phase2DueDateDays: t.phase2DueDateDays,
          phase2Required: t.phase2Required,
          checklistType: t.checklistType,
          gobodoKey: t.key, // Store Gobodo key for relationship
        }));
      // Sort by the defined order
      return filtered.sort((a, b) => 
        STANDARD_AUDIT_TYPES_ORDER.indexOf(a.name) - STANDARD_AUDIT_TYPES_ORDER.indexOf(b.name)
      );
    }
    return AUDIT_TEMPLATES.map(t => ({ ...t, gobodoKey: undefined }));
  }, [gobodoTemplates]);

  // Build auditor options from real users
  const auditorOptions = useMemo(() => {
    const options = users
      .map(user => ({
        id: user.id || '',
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || ''
      }))
      .filter(opt => opt.name.length > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
    // Add --None-- option at the beginning
    return [{ id: '', name: '--None--' }, ...options];
  }, [users]);

  // Generate auto-number audit name
  const generateAuditName = () => {
    const num = Math.floor(10000 + Math.random() * 90000);
    return `CyberAudit-${num}`;
  };

  const [formData, setFormData] = useState({
    // Section 1: Information
    auditName: generateAuditName(),
    accountId: clientId,
    accountName: clientName,
    auditor: '',
    dueDate: '',
    fiscalYear: new Date().getFullYear().toString(),
    auditType: '',
    auditTypeName: '',
    auditTypeGobodoKey: '', // Gobodo template key for relationship

    // Section 2: Phase History
    phaseStatus: 'Not Started',
    roadBlockNote: '',
    phase1DueDate: '',
    phase2DueDate: '',
    phase1StartDate: '',
    phase1StartTime: '',
    phase2StartDate: '',
    phase2StartTime: '',
    phase1CompletedDate: '',
    phase1CompletedTime: '',
    phase2CompletedDate: '',
    phase2CompletedTime: '',

    // Section 3: System Information (auto-filled)
    createdBy: 'Current User',
    lastModifiedBy: 'Current User'
  });

  const [auditTypeSearch, setAuditTypeSearch] = useState('');
  const [showAuditTypeDropdown, setShowAuditTypeDropdown] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [phase2Disabled, setPhase2Disabled] = useState(false);

  // Auto-calculate due dates when template or start date changes
  const recalculateDueDates = useCallback((template: { phase1DueDateDays?: number; phase2DueDateDays?: number; phase2Required?: boolean } | null, startDateStr: string) => {
    if (!template || !startDateStr) return;

    // Get due date days from template (works with both Gobodo and local templates)
    const phase1Days = template.phase1DueDateDays ?? 7;
    const phase2Days = template.phase2DueDateDays ?? 0;
    const phase2Required = template.phase2Required ?? false;

    const startDate = new Date(startDateStr);
    if (isNaN(startDate.getTime())) return;

    // Calculate Phase 1 due date
    const phase1Due = new Date(startDate);
    phase1Due.setDate(phase1Due.getDate() + phase1Days);

    // Calculate Phase 2 due date if required
    const phase2Due = phase2Required
      ? new Date(phase1Due.getTime() + phase2Days * 24 * 60 * 60 * 1000)
      : null;

    // Overall due date: if Phase 2 required, use phase2Due; otherwise use phase1Due
    const overallDueDate = phase2Required && phase2Due ? phase2Due : phase1Due;

    setFormData(prev => ({
      ...prev,
      dueDate: formatDateForInput(overallDueDate),
      phase1DueDate: formatDateForInput(phase1Due),
      phase2DueDate: phase2Due ? formatDateForInput(phase2Due) : '',
    }));
    setPhase2Disabled(!phase2Required);
  }, []);

  // Update accountName/accountId when props change OR when modal opens
  // This ensures the clientId is always set even if modal stays mounted
  useEffect(() => {
    if (isOpen && clientId) {
      setFormData(prev => {
        if (prev.accountId !== clientId || prev.accountName !== clientName) {
          return { ...prev, accountName: clientName, accountId: clientId };
        }
        return prev;
      });
    }
  }, [isOpen, clientName, clientId]);

  if (!isOpen) return null;

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAuditTypeSelect = (template: { id: string; name: string; gobodoKey?: string }) => {
    setFormData(prev => ({
      ...prev,
      auditType: template.id,
      auditTypeName: template.name,
      auditTypeGobodoKey: template.gobodoKey || template.id // Store Gobodo key for relationship
    }));
    setSelectedTemplateId(template.id);
    setAuditTypeSearch('');
    setShowAuditTypeDropdown(false);

    // Auto-calculate due dates if we have a start date (use Phase 1 Start Date as the base)
    // If no Phase 1 Start Date, use today as default
    const startDateStr = formData.phase1StartDate || formatDateForInput(new Date());
    // Find full template with due date days from availableTemplates
    const fullTemplate = availableTemplates.find(t => t.id === template.id);
    if (fullTemplate) {
      recalculateDueDates(fullTemplate, startDateStr);
    }
  };

  const clearAuditType = () => {
    setFormData(prev => ({ ...prev, auditType: '', auditTypeName: '', auditTypeGobodoKey: '', dueDate: '', phase1DueDate: '', phase2DueDate: '' }));
    setSelectedTemplateId(null);
    setPhase2Disabled(false);
  };

  const clearAccount = () => {
    setFormData(prev => ({ ...prev, accountId: '', accountName: '' }));
  };

  // Handle start date change - recalculate due dates
  const handleStartDateChange = (dateStr: string) => {
    setFormData(prev => ({ ...prev, phase1StartDate: dateStr }));
    if (selectedTemplateId) {
      const selectedTemplate = availableTemplates.find(t => t.id === selectedTemplateId);
      if (selectedTemplate) {
        recalculateDueDates(selectedTemplate, dateStr);
      }
    }
  };

  const filteredAuditTypes = availableTemplates.filter(template =>
    template.name.toLowerCase().includes(auditTypeSearch.toLowerCase())
  );

  const handleSave = (saveAndNew: boolean = false) => {
    // Find the selected auditor's name from the options
    const selectedAuditor = auditorOptions.find(opt => opt.id === formData.auditor);
    const auditorName = selectedAuditor?.name || '';

    const auditData = {
      ...formData,
      clientId: formData.accountId,
      clientName: formData.accountName,
      // Include auditor name for display (not just ID)
      auditorName: auditorName,
      // Use actual current user's name
      createdBy: currentUserName || 'System',
      lastModifiedBy: currentUserName || 'System',
      createdDate: new Date().toISOString(),
      lastModifiedDate: new Date().toISOString()
    };

    onSave(auditData, saveAndNew);

    if (saveAndNew) {
      // Reset form with new audit name
      setFormData({
        auditName: generateAuditName(),
        accountId: clientId,
        accountName: clientName,
        auditor: '',
        dueDate: '',
        fiscalYear: new Date().getFullYear().toString(),
        auditType: '',
        auditTypeName: '',
        auditTypeGobodoKey: '',
        phaseStatus: 'Not Started',
        roadBlockNote: '',
        phase1DueDate: '',
        phase2DueDate: '',
        phase1StartDate: '',
        phase1StartTime: '',
        phase2StartDate: '',
        phase2StartTime: '',
        phase1CompletedDate: '',
        phase1CompletedTime: '',
        phase2CompletedDate: '',
        phase2CompletedTime: '',
        createdBy: 'Current User',
        lastModifiedBy: 'Current User'
      });
      // Reset template-related state
      setSelectedTemplateId(null);
      setPhase2Disabled(false);
    } else {
      onClose();
    }
  };

  // Section header component
  const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
    <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2 border-b border-gray-200 dark:border-gray-600">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">
        {title}
      </h3>
    </div>
  );

  // Required label component
  const RequiredLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {required && <span className="text-red-500 mr-1">*</span>}
      {children}
    </label>
  );

  // Lookup field with icon and clear button
  const LookupField: React.FC<{
    label: string;
    value: string;
    onClear: () => void;
    required?: boolean;
    disabled?: boolean;
    icon?: React.ReactNode;
  }> = ({ label, value, onClear, required, disabled, icon }) => (
    <div>
      <RequiredLabel required={required}>{label}</RequiredLabel>
      <div className="relative">
        <div className={`flex items-center w-full px-3 py-2 border rounded-md ${
          disabled
            ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600'
            : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'
        }`}>
          {icon && <span className="mr-2 text-gray-400">{icon}</span>}
          <span className={`flex-1 text-sm ${value ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}`}>
            {value || 'None'}
          </span>
          {value && !disabled && (
            <button
              type="button"
              onClick={onClear}
              className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">New CS Audit</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* SECTION 1: Information */}
          <SectionHeader title="Information" />
          <div className="p-6 grid grid-cols-2 gap-x-8 gap-y-4">
            {/* Row 1: Audit Name | Due Date */}
            <div>
              <RequiredLabel>Audit Name</RequiredLabel>
              <input
                type="text"
                value={formData.auditName}
                disabled
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-md text-sm"
              />
            </div>
            <div>
              <RequiredLabel required>Due Date</RequiredLabel>
              <div className="relative">
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => handleInputChange('dueDate', e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Row 2: Account | Fiscal Year */}
            <LookupField
              label="Account"
              value={formData.accountName}
              onClear={clearAccount}
              required
              icon={<Building2 className="h-4 w-4" />}
            />
            <div>
              <RequiredLabel required>Fiscal Year</RequiredLabel>
              <input
                type="text"
                value={formData.fiscalYear}
                onChange={(e) => handleInputChange('fiscalYear', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="2025"
              />
            </div>

            {/* Row 3: Auditor | Audit Type */}
            <div>
              <RequiredLabel>Auditor</RequiredLabel>
              <div className="relative">
                <select
                  value={formData.auditor}
                  onChange={(e) => handleInputChange('auditor', e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {auditorOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
            <div>
              <RequiredLabel required>Audit Type</RequiredLabel>
              <div className="relative">
                {formData.auditTypeName ? (
                  <div className="flex items-center w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-md">
                    <span className="flex-1 text-sm text-gray-900 dark:text-gray-100">{formData.auditTypeName}</span>
                    <button
                      type="button"
                      onClick={clearAuditType}
                      className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={auditTypeSearch}
                        onChange={(e) => {
                          setAuditTypeSearch(e.target.value);
                          setShowAuditTypeDropdown(true);
                        }}
                        onFocus={() => setShowAuditTypeDropdown(true)}
                        placeholder="Search Cyber Sweep Audit Templates..."
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    {showAuditTypeDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {filteredAuditTypes.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-500">No templates found</div>
                        ) : (
                          filteredAuditTypes.map((template) => (
                            <button
                              key={template.id}
                              type="button"
                              onClick={() => handleAuditTypeSelect(template)}
                              className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600"
                            >
                              <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">{template.name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                P1: +{template.phase1DueDateDays} days
                                {template.phase2Required ? ` • P2: +${template.phase2DueDateDays} days after P1` : ' • No Phase 2'}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* SECTION 2: Phase History */}
          <SectionHeader title="Phase History" />
          <div className="p-6 grid grid-cols-2 gap-x-8 gap-y-4">
            {/* Row 1: Phase Status | Road Block Note */}
            <div>
              <RequiredLabel>Phase Status</RequiredLabel>
              <div className="relative">
                <select
                  value={formData.phaseStatus}
                  onChange={(e) => handleInputChange('phaseStatus', e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {PHASE_STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
            <div>
              <RequiredLabel>Road Block Note</RequiredLabel>
              <textarea
                value={formData.roadBlockNote}
                onChange={(e) => handleInputChange('roadBlockNote', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm resize-y focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter any roadblocks..."
              />
            </div>

            {/* Row 2: Phase 1 Due Date | Phase 2 Due Date */}
            <div>
              <RequiredLabel required>Phase 1 Due Date</RequiredLabel>
              <div className="relative">
                <input
                  type="date"
                  value={formData.phase1DueDate}
                  onChange={(e) => handleInputChange('phase1DueDate', e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
            <div>
              <RequiredLabel required={!phase2Disabled}>Phase 2 Due Date</RequiredLabel>
              <div className="relative">
                <input
                  type="date"
                  value={formData.phase2DueDate}
                  onChange={(e) => handleInputChange('phase2DueDate', e.target.value)}
                  disabled={phase2Disabled}
                  className={`w-full px-3 py-2 pr-10 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    phase2Disabled
                      ? 'border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                  }`}
                />
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
              {phase2Disabled && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  Phase 2 not required for this audit type
                </p>
              )}
            </div>

            {/* Row 3: Phase 1 Start Date/Time | Phase 2 Start Date/Time */}
            <div>
              <RequiredLabel>Phase 1 Start Date/Time</RequiredLabel>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="date"
                    value={formData.phase1StartDate}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
                <div className="relative w-32">
                  <input
                    type="time"
                    value={formData.phase1StartTime}
                    onChange={(e) => handleInputChange('phase1StartTime', e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>
            <div>
              <RequiredLabel>Phase 2 Start Date/Time</RequiredLabel>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="date"
                    value={formData.phase2StartDate}
                    onChange={(e) => handleInputChange('phase2StartDate', e.target.value)}
                    disabled={phase2Disabled}
                    className={`w-full px-3 py-2 pr-10 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      phase2Disabled
                        ? 'border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                    }`}
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
                <div className="relative w-32">
                  <input
                    type="time"
                    value={formData.phase2StartTime}
                    onChange={(e) => handleInputChange('phase2StartTime', e.target.value)}
                    disabled={phase2Disabled}
                    className={`w-full px-3 py-2 pr-10 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      phase2Disabled
                        ? 'border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                    }`}
                  />
                  <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Row 4: Phase 1 Completed Date/Time | Phase 2 Completed Date/Time */}
            <div>
              <RequiredLabel>Phase 1 Completed Date/Time</RequiredLabel>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="date"
                    value={formData.phase1CompletedDate}
                    onChange={(e) => handleInputChange('phase1CompletedDate', e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
                <div className="relative w-32">
                  <input
                    type="time"
                    value={formData.phase1CompletedTime}
                    onChange={(e) => handleInputChange('phase1CompletedTime', e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>
            <div>
              <RequiredLabel>Phase 2 Completed Date/Time</RequiredLabel>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="date"
                    value={formData.phase2CompletedDate}
                    onChange={(e) => handleInputChange('phase2CompletedDate', e.target.value)}
                    disabled={phase2Disabled}
                    className={`w-full px-3 py-2 pr-10 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      phase2Disabled
                        ? 'border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                    }`}
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
                <div className="relative w-32">
                  <input
                    type="time"
                    value={formData.phase2CompletedTime}
                    onChange={(e) => handleInputChange('phase2CompletedTime', e.target.value)}
                    disabled={phase2Disabled}
                    className={`w-full px-3 py-2 pr-10 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      phase2Disabled
                        ? 'border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                    }`}
                  />
                  <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 3: System Information */}
          <SectionHeader title="System Information" />
          <div className="p-6 grid grid-cols-2 gap-x-8 gap-y-4">
            <div>
              <RequiredLabel>Created By</RequiredLabel>
              <input
                type="text"
                value={currentUserName || 'Current User'}
                disabled
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-md text-sm"
              />
            </div>
            <div>
              <RequiredLabel>Last Modified By</RequiredLabel>
              <input
                type="text"
                value={currentUserName || 'Current User'}
                disabled
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-md text-sm"
              />
            </div>
          </div>
        </div>

        {/* Footer with Buttons */}
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-6 py-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => handleSave(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700 transition-colors"
          >
            Save & New
          </button>
          <button
            type="button"
            onClick={() => handleSave(false)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700 transition-colors"
          >
            Save
          </button>
        </div>
      </div>

      {/* Click outside to close audit type dropdown */}
      {showAuditTypeDropdown && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowAuditTypeDropdown(false)}
        />
      )}
    </div>
  );
};
